import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import { Search, Clear, FilterList } from '@mui/icons-material';

interface EventFiltersProps {
  filters: {
    title?: string;
    status?: string;
    hasTickets?: boolean;
    archived?: boolean;
  };
  onFiltersChange: (filters: {
    title?: string;
    status?: string;
    hasTickets?: boolean;
    archived?: boolean;
  }) => void;
  onClearFilters: () => void;
}

export function EventFilters({ filters, onFiltersChange, onClearFilters }: EventFiltersProps) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [showFilters, setShowFilters] = useState(false);

  // Sincronizar campos locais com filtros aplicados
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (field: string, value: any) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
  };

  const handleClearFilters = () => {
    setLocalFilters({});
    onClearFilters();
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey as keyof typeof newFilters];
    onFiltersChange(newFilters);
  };

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== undefined && value !== '' && value !== null
  );

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Aberto';
      case 'closed': return 'Fechado';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
          color={hasActiveFilters ? 'primary' : 'inherit'}
        >
          Filtros {hasActiveFilters && `(${Object.keys(filters).filter(key => filters[key as keyof typeof filters] !== undefined).length})`}
        </Button>
        
        {hasActiveFilters && (
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={handleClearFilters}
            size="small"
            color="error"
          >
            Limpar todos
          </Button>
        )}
      </Box>

      {showFilters && (
        <Box sx={{ 
          p: 2, 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 1,
          backgroundColor: 'background.paper'
        }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar por título"
                value={localFilters.title || ''}
                onChange={(e) => handleFilterChange('title', e.target.value)}
                placeholder="Digite o título do evento..."
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={localFilters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="open">Aberto</MenuItem>
                  <MenuItem value="closed">Fechado</MenuItem>
                  <MenuItem value="cancelled">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ingressos</InputLabel>
                <Select
                  value={localFilters.hasTickets === undefined ? '' : localFilters.hasTickets.toString()}
                  onChange={(e) => handleFilterChange('hasTickets', e.target.value === '' ? undefined : e.target.value === 'true')}
                  label="Ingressos"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Com ingressos</MenuItem>
                  <MenuItem value="false">Sem ingressos</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  startIcon={<Search />}
                  fullWidth
                >
                  Buscar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {hasActiveFilters && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {filters.title && (
              <Chip
                label={`Título: ${filters.title}`}
                onDelete={() => handleRemoveFilter('title')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {filters.status && (
              <Chip
                label={`Status: ${getStatusLabel(filters.status)}`}
                onDelete={() => handleRemoveFilter('status')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {filters.hasTickets !== undefined && (
              <Chip
                label={`Ingressos: ${filters.hasTickets ? 'Sim' : 'Não'}`}
                onDelete={() => handleRemoveFilter('hasTickets')}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
